<template>
  <div class="d-flex flex-column flex-lg-row min-vh-100 bg-light">
    <!-- SIDEBAR -->
    <Sidebar
      :class="{ 'd-none d-lg-flex': !sidebarOpen }"
      @close-sidebar="sidebarOpen = false"
    />

    <!-- CONTENIDO PRINCIPAL -->
    <div class="main-content">
      <Header @toggle-sidebar="sidebarOpen = !sidebarOpen" />

      <main class="container-fluid p-4">
        <!-- ERROR -->
        <div
          v-if="error"
          class="alert alert-danger alert-dismissible fade show"
        >
          <i class="bi bi-exclamation-triangle me-2"></i>

          {{ error }}

          <button
            type="button"
            class="btn-close"
            @click="error = null"
          ></button>
        </div>

        <!--
          El módulo se dibuja de inmediato: encabezado, filtros y encabezado de
          la tabla. El estado de carga vive solo dentro del tbody, así al
          entrar o al cambiar de página no se vuelve a montar toda la pantalla.
        -->
        <div v-if="!permisos || permisos?.listar">
          <!-- ENCABEZADO -->
          <div
            class="d-flex justify-content-between align-items-center mb-4"
          >
            <h2 class="fw-bold">
              Productos
            </h2>

            <button
              v-if="permisos?.crear"
              type="button"
              class="btn btn-primary"
              @click="openAddModal"
            >
              <i class="bi bi-plus-circle me-2"></i>
              Nuevo Producto
            </button>
          </div>

          <!-- FILTROS (se consultan contra la base de datos) -->
          <div class="card shadow-sm mb-3">
            <div class="card-body productos-filtros">
              <div class="row g-2 align-items-end">
                <div class="col-12 col-md-3">
                  <label class="form-label mb-1">
                    Código
                  </label>

                  <input
                    v-model.trim="filtroCodigo"
                    type="text"
                    class="form-control"
                    placeholder="Ej. PROD-001"
                    @keyup.enter="aplicarFiltros"
                  />
                </div>

                <div class="col-12 col-md-3">
                  <label class="form-label mb-1">
                    Nombre
                  </label>

                  <input
                    v-model.trim="filtroNombre"
                    type="text"
                    class="form-control"
                    placeholder="Ej. Mouse"
                    @keyup.enter="aplicarFiltros"
                  />
                </div>

                <div class="col-6 col-md-2">
                  <label class="form-label mb-1">
                    Alta desde
                  </label>

                  <input
                    v-model="filtroDesde"
                    type="date"
                    class="form-control"
                  />
                </div>

                <div class="col-6 col-md-2">
                  <label class="form-label mb-1">
                    Alta hasta
                  </label>

                  <input
                    v-model="filtroHasta"
                    type="date"
                    class="form-control"
                  />
                </div>

                <div class="col-12 col-md-2 d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-primary flex-fill"
                    :disabled="loading"
                    @click="aplicarFiltros"
                  >
                    Buscar
                  </button>

                  <button
                    type="button"
                    class="btn btn-outline-secondary flex-fill"
                    :disabled="loading"
                    @click="limpiarFiltros"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- TABLA -->
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="table-responsive">
                <table
                  class="table table-hover align-middle w-100"
                >
                  <thead class="table-light">
                    <tr>
                      <th>#</th>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Precio Unitario</th>
                      <th>Precio Compra</th>
                      <th>Ganancia</th>
                      <th>Stock</th>
                      <th>Activo</th>
                      <th>Categoría</th>
                      <th>Medida</th>
                      <th>Alta</th>

                      <th
                        v-if="
                          permisos?.actualizar ||
                          permisos?.eliminar
                        "
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <!-- CARGANDO: solo el cuerpo de la tabla -->
                    <tr v-if="loading">
                      <td
                        :colspan="totalColumnas"
                        class="text-center text-muted py-4"
                      >
                        <span
                          class="spinner-border spinner-border-sm me-2"
                        ></span>

                        Cargando productos...
                      </td>
                    </tr>

                    <template v-else>
                      <tr
                        v-for="(producto, index) in Productos"
                        :key="producto.id"
                      >
                        <td>
                          {{ numeroFila(index) }}
                        </td>

                        <td>
                          {{ producto.codigo_producto }}
                        </td>

                        <td>
                          {{ producto.nombre }}
                        </td>

                        <td>
                          {{ producto.precio_unitario }}
                        </td>

                        <td>
                          {{ producto.precio_compra }}
                        </td>

                        <td>
                          {{ producto.ganancia }}
                        </td>

                        <td>
                          {{ producto.stock }}
                        </td>

                        <td>
                          <span
                            :class="
                              producto.activo
                                ? 'badge bg-success'
                                : 'badge bg-secondary'
                            "
                          >
                            {{ producto.activo ? 'Sí' : 'No' }}
                          </span>
                        </td>

                        <td>
                          {{
                            producto.categoria
                              ?.nombre_categoria || '-'
                          }}
                        </td>

                        <td>
                          {{
                            producto.medida
                              ?.unidad_sat || '-'
                          }}
                        </td>

                        <td>
                          {{ fechaAlta(producto) }}
                        </td>

                        <td
                          v-if="
                            permisos?.actualizar ||
                            permisos?.eliminar
                          "
                        >
                          <button
                            v-if="permisos?.actualizar"
                            type="button"
                            class="btn btn-sm btn-warning me-2"
                            @click="openEditModal(producto)"
                          >
                            <i class="bi bi-pencil"></i>
                          </button>

                          <button
                            v-if="permisos?.eliminar"
                            type="button"
                            class="btn btn-sm btn-danger"
                            @click="deleteProducto(producto.id)"
                          >
                            <i class="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>

                      <tr v-if="Productos.length === 0">
                        <td
                          :colspan="totalColumnas"
                          class="text-center text-muted py-4"
                        >
                          {{
                            hayFiltros
                              ? 'No hay productos con esos filtros.'
                              : 'No hay productos registrados'
                          }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

              <!--
                PAGINACIÓN: cada botón dispara una consulta nueva al API.
                El navegador solo conserva la página actual.
              -->
              <div
                class="productos-paginacion d-flex flex-wrap justify-content-between align-items-center gap-2 pt-3"
              >
                <small class="text-muted">
                  Mostrando {{ Productos.length }} de
                  {{ meta.total }} &middot;
                  Página {{ meta.current_page }} de
                  {{ meta.last_page }}
                </small>

                <div class="d-flex align-items-center gap-2">
                  <select
                    v-model.number="porPagina"
                    class="form-select form-select-sm w-auto"
                    :disabled="loading"
                    @change="cambiarPorPagina"
                  >
                    <option :value="5">
                      5 por página
                    </option>

                    <option :value="15">
                      15 por página
                    </option>

                    <option :value="30">
                      30 por página
                    </option>

                    <option :value="50">
                      50 por página
                    </option>
                  </select>

                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="loading || meta.current_page <= 1"
                    @click="irPagina(meta.current_page - 1)"
                  >
                    &lsaquo; Anterior
                  </button>

                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="
                      loading ||
                      meta.current_page >= meta.last_page
                    "
                    @click="irPagina(meta.current_page + 1)"
                  >
                    Siguiente &rsaquo;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SIN PERMISO PARA LISTAR -->
        <div
          v-if="permisos && !permisos?.listar"
          class="alert alert-warning text-center"
        >
          <i class="bi bi-shield-lock me-2"></i>
          No tienes permiso para ver los productos.
        </div>
      </main>
    </div>

    <!-- OVERLAY MÓVIL -->
    <div
      v-if="sidebarOpen"
      class="sidebar-overlay d-lg-none"
      @click="sidebarOpen = false"
    ></div>

    <!-- MODAL CREAR/EDITAR -->
    <BaseModal
      v-model:visible="modalVisible"
      :title="modalTitle"
    >
      <div class="row g-3">
        <!-- CÓDIGO -->
        <div class="col-md-4">
          <label class="form-label">
            Código
          </label>

          <input
            v-model.trim="ProductoCodigo"
            type="text"
            class="form-control"
            placeholder="Código"
          />
        </div>

        <!-- NOMBRE -->
        <div class="col-md-4">
          <label class="form-label">
            Nombre
          </label>

          <input
            v-model.trim="ProductoNombre"
            type="text"
            class="form-control"
            placeholder="Nombre"
          />
        </div>

        <!-- PRECIO UNITARIO -->
        <div class="col-md-4">
          <label class="form-label">
            Precio Unitario
          </label>

          <input
            v-model.number="ProductoPrecioUnitario"
            type="number"
            min="0"
            step="0.01"
            class="form-control"
            placeholder="Precio Unitario"
          />
        </div>

        <!-- PRECIO COMPRA -->
        <div class="col-md-4">
          <label class="form-label">
            Precio Compra
          </label>

          <input
            v-model.number="ProductoPrecioCompra"
            type="number"
            min="0"
            step="0.01"
            class="form-control"
            placeholder="Precio Compra"
          />
        </div>

        <!-- GANANCIA -->
        <div class="col-md-4">
          <label class="form-label">
            Ganancia
          </label>

          <input
            v-model.number="ProductoGanancia"
            type="number"
            min="0"
            step="0.01"
            class="form-control"
            placeholder="Ganancia"
          />
        </div>

        <!-- STOCK -->
        <div class="col-md-4">
          <label class="form-label">
            Stock
          </label>

          <input
            v-model.number="ProductoStock"
            type="number"
            min="0"
            step="0.001"
            class="form-control"
            placeholder="Stock"
          />
        </div>

        <!-- CLAVE SAT -->
        <div class="col-md-4">
          <label class="form-label">
            Clave Producto/Servicio SAT
          </label>

          <input
            v-model.trim="ProductoClavePS"
            type="text"
            class="form-control"
            placeholder="Clave PS"
          />
        </div>

        <!-- CLAVE UNIDAD -->
        <div class="col-md-4">
          <label class="form-label">
            Clave Unidad SAT
          </label>

          <input
            v-model.trim="ProductoClaveUnidad"
            type="text"
            class="form-control"
            placeholder="Clave Unidad"
          />
        </div>

        <!-- ACTIVO -->
        <div class="col-md-4 d-flex align-items-center">
          <div class="form-check mt-4">
            <input
              id="activoCheck"
              v-model="ProductoActivo"
              type="checkbox"
              class="form-check-input"
            />

            <label
              class="form-check-label"
              for="activoCheck"
            >
              Activo
            </label>
          </div>
        </div>

        <!-- CATEGORÍA -->
        <div class="col-md-4">
          <label class="form-label">
            Categoría
          </label>

          <select
            v-model.number="ProductoCategoria"
            class="form-select"
          >
            <option value="">
              Seleccione una categoría
            </option>

            <option
              v-for="cat in Categorias"
              :key="cat.id"
              :value="cat.id"
            >
              {{ cat.nombre_categoria }}
            </option>
          </select>
        </div>

        <!-- MEDIDA -->
        <div class="col-md-4">
          <label class="form-label">
            Medida
          </label>

          <select
            v-model.number="ProductoMedida"
            class="form-select"
          >
            <option value="">
              Seleccione una medida
            </option>

            <option
              v-for="med in Medidas"
              :key="med.id"
              :value="med.id"
            >
              {{ med.nombre_largo }}
            </option>
          </select>
        </div>

        <!-- OBJETO DE IMPUESTO -->
        <div class="col-md-4">
          <label class="form-label">
            Objeto de Impuesto
          </label>

          <select
            v-model.number="ProductoObjetoId"
            class="form-select"
          >
            <option value="">
              Seleccione un objeto
            </option>

            <option
              v-for="obj in Objetos"
              :key="obj.id"
              :value="obj.id"
            >
              {{ obj.clave_objeto }} -
              {{ obj.descripcion_objeto }}
            </option>
          </select>
        </div>

        <!-- IMPUESTO -->
        <div class="col-md-4">
          <label class="form-label">
            Impuesto
          </label>

          <select
            v-model.number="ProductoImpuestoId"
            class="form-select"
          >
            <option value="">
              Seleccione un impuesto
            </option>

            <option
              v-for="imp in Impuestos"
              :key="imp.id"
              :value="imp.id"
            >
              {{ imp.clave_impuesto }} -
              {{ imp.descripcion_impuesto }}
            </option>
          </select>
        </div>

        <!-- TIPO FACTOR -->
        <div class="col-md-4">
          <label class="form-label">
            Tipo Factor
          </label>

          <select
            v-model.number="ProductoTipoFactorId"
            class="form-select"
          >
            <option value="">
              Seleccione un tipo factor
            </option>

            <option
              v-for="tip in TiposFactor"
              :key="tip.id"
              :value="tip.id"
            >
              {{ tip.clave_tipofactor }} -
              {{ tip.descripcion_tipofactor }}
            </option>
          </select>
        </div>

        <!-- CONFIGURACIÓN DE IMPUESTO -->
        <div class="col-md-4">
          <label class="form-label">
            Configuración de Impuesto
          </label>

          <select
            v-model.number="
              ProductoImpuestoConfiguracionId
            "
            class="form-select"
          >
            <option value="">
              Seleccione una configuración
            </option>

            <option
              v-for="config in ImpuestoConfiguraciones"
              :key="config.id"
              :value="config.id"
            >
              {{ obtenerNombreConfiguracion(config) }}
            </option>
          </select>

          <small
            v-if="ImpuestoConfiguraciones.length === 0"
            class="text-danger d-block mt-1"
          >
            No hay configuraciones de impuesto disponibles
          </small>
        </div>
      </div>

      <template #footer>
        <button
          type="button"
          class="btn btn-secondary"
          @click="modalVisible = false"
        >
          Cancelar
        </button>

        <button
          v-if="
            editingId
              ? permisos?.actualizar
              : permisos?.crear
          "
          type="button"
          class="btn btn-primary"
          :disabled="guardando"
          @click="saveProducto"
        >
          <span
            v-if="guardando"
            class="spinner-border spinner-border-sm me-2"
          ></span>

          {{ editingId ? 'Actualizar' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <!-- MODAL DE CONFIRMACIÓN -->
    <ConfirmModal
      v-model:visible="confirmModalVisible"
      :message="confirmMessage"
      @confirmed="onConfirmDelete"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount
} from 'vue';

import api, {
  obtenerPermisosPorModulo
} from '@/services/api.js';

import Header from '@/components/HeaderVue.vue';
import Sidebar from '@/components/Sidebar.vue';
import BaseModal from '@/components/BaseModal.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

export default {
  name: 'ProductosViews',

  components: {
    Header,
    Sidebar,
    BaseModal,
    ConfirmModal
  },

  setup() {
    const sidebarOpen = ref(false);

    /*
     * null significa que los permisos aún no cargan.
     */
    const permisos = ref(null);

    const Productos = ref([]);
    const Medidas = ref([]);
    const Categorias = ref([]);
    const Objetos = ref([]);
    const Impuestos = ref([]);
    const TiposFactor = ref([]);
    const ImpuestoConfiguraciones = ref([]);

    const loading = ref(false);
    const error = ref(null);

    const modalVisible = ref(false);
    const modalTitle = ref('');
    const editingId = ref(null);

    const ProductoCodigo = ref('');
    const ProductoNombre = ref('');
    const ProductoPrecioUnitario = ref(0);
    const ProductoPrecioCompra = ref(0);
    const ProductoGanancia = ref(0);
    const ProductoStock = ref(0);
    const ProductoActivo = ref(true);
    const ProductoCategoria = ref('');
    const ProductoMedida = ref('');
    const ProductoClavePS = ref('');
    const ProductoClaveUnidad = ref('');
    const ProductoObjetoId = ref('');
    const ProductoImpuestoId = ref('');
    const ProductoTipoFactorId = ref('');
    const ProductoImpuestoConfiguracionId = ref('');

    const confirmModalVisible = ref(false);
    const confirmMessage = ref('');
    const deletingId = ref(null);

    /*
    |--------------------------------------------------------------------------
    | Filtros y paginación (se resuelven en el servidor)
    |--------------------------------------------------------------------------
    */
    const filtroCodigo = ref('');
    const filtroNombre = ref('');
    const filtroDesde = ref('');
    const filtroHasta = ref('');

    const pagina = ref(1);
    const porPagina = ref(15);

    const meta = ref({
      total: 0,
      current_page: 1,
      per_page: 15,
      last_page: 1
    });

    /*
     * Identifica la petición en curso: si el usuario cambia de página o de
     * filtro antes de que responda la anterior, la respuesta vieja se descarta.
     */
    let peticionActual = 0;

    /*
     * Se separa de 'loading' para que guardar o eliminar no vacíe la tabla.
     */
    const guardando = ref(false);

    const hayFiltros = computed(() => {
      return !!(
        filtroCodigo.value ||
        filtroNombre.value ||
        filtroDesde.value ||
        filtroHasta.value
      );
    });

    const totalColumnas = computed(() => {
      return permisos.value?.actualizar ||
        permisos.value?.eliminar
        ? 12
        : 11;
    });

    const numeroFila = (index) => {
      const pag = meta.value.current_page || 1;
      const porPag = meta.value.per_page || porPagina.value;

      return (pag - 1) * porPag + index + 1;
    };

    const fechaAlta = (producto) => {
      const valor =
        producto?.fecha_registro ||
        producto?.created_at;

      if (!valor) {
        return '-';
      }

      return String(valor).slice(0, 10);
    };

    const permisoActivo = (valor) => {
      return (
        valor === true ||
        valor === 1 ||
        valor === '1' ||
        valor === 'true'
      );
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar permisos del módulo productos
    |--------------------------------------------------------------------------
    */
    const fetchPermisos = async () => {
      try {
        const respuesta =
          await obtenerPermisosPorModulo('productos');

        permisos.value = {
          listar: permisoActivo(
            respuesta?.listar
          ),

          crear: permisoActivo(
            respuesta?.crear
          ),

          actualizar: permisoActivo(
            respuesta?.actualizar
          ),

          eliminar: permisoActivo(
            respuesta?.eliminar
          )
        };
      } catch (e) {
        permisos.value = {
          listar: false,
          crear: false,
          actualizar: false,
          eliminar: false
        };

        error.value =
          'No fue posible cargar los permisos de productos.';
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Configuraciones de impuestos
    |--------------------------------------------------------------------------
    */
    const extraerConfiguraciones = (data) => {
      const posiblesListas = [
        data,
        data?.data,
        data?.configuraciones,
        data?.impuesto_configuraciones,
        data?.data?.configuraciones,
        data?.data?.impuesto_configuraciones
      ];

      const listaEncontrada =
        posiblesListas.find((item) =>
          Array.isArray(item)
        );

      return listaEncontrada || [];
    };

    const obtenerNombreConfiguracion = (
      config
    ) => {
      const nombre =
        config.nombre ??
        config.nombre_configuracion ??
        config.descripcion ??
        config.descripcion_configuracion;

      if (nombre) {
        return nombre;
      }

      const impuesto =
        config.impuesto?.descripcion_impuesto ??
        config.descripcion_impuesto ??
        config.clave_impuesto ??
        '';

      const tipoFactor =
        config.tipo_factor
          ?.descripcion_tipofactor ??
        config.descripcion_tipofactor ??
        config.clave_tipofactor ??
        '';

      const tasa =
        config.tasa_o_cuota ??
        config.tasa ??
        config.porcentaje ??
        '';

      const partes = [
        impuesto,
        tipoFactor,
        tasa !== '' ? tasa : ''
      ].filter(
        (item) =>
          item !== null &&
          item !== undefined &&
          item !== ''
      );

      if (partes.length > 0) {
        return partes.join(' - ');
      }

      return `Configuración ${config.id}`;
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar productos (bajo demanda: solo la página actual)
    |--------------------------------------------------------------------------
    | Cada llamada consulta la base de datos con los filtros vigentes. El
    | navegador nunca conserva más registros que los de la página mostrada.
    */
    const fetchProductos = async () => {
      if (!permisos.value?.listar) {
        Productos.value = [];
        return;
      }

      const idPeticion = ++peticionActual;

      loading.value = true;
      error.value = null;

      try {
        const response = await api.get('/productos', {
          params: {
            codigo: filtroCodigo.value || undefined,
            nombre: filtroNombre.value || undefined,
            desde: filtroDesde.value || undefined,
            hasta: filtroHasta.value || undefined,
            page: pagina.value,
            per_page: porPagina.value
          }
        });

        // Llegó tarde: ya se pidió otra página u otro filtro.
        if (idPeticion !== peticionActual) {
          return;
        }

        Productos.value = response.data?.data || [];

        const metaRespuesta = response.data?.meta;

        if (metaRespuesta) {
          meta.value = metaRespuesta;
          pagina.value = metaRespuesta.current_page;
        } else {
          meta.value = {
            total: response.data?.total || 0,
            current_page: pagina.value,
            per_page: porPagina.value,
            last_page: 1
          };
        }
      } catch (e) {
        if (idPeticion !== peticionActual) {
          return;
        }

        Productos.value = [];

        error.value =
          'Error al cargar productos: ' +
          (
            e.response?.data?.message ||
            e.message
          );
      } finally {
        if (idPeticion === peticionActual) {
          loading.value = false;
        }
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Acciones de filtro y paginación
    |--------------------------------------------------------------------------
    | Todas vuelven a consultar la base de datos; nada se filtra en memoria.
    */
    const aplicarFiltros = () => {
      pagina.value = 1;
      fetchProductos();
    };

    const limpiarFiltros = () => {
      filtroCodigo.value = '';
      filtroNombre.value = '';
      filtroDesde.value = '';
      filtroHasta.value = '';
      pagina.value = 1;

      fetchProductos();
    };

    const irPagina = (n) => {
      if (
        n < 1 ||
        n > (meta.value.last_page || 1) ||
        n === meta.value.current_page
      ) {
        return;
      }

      pagina.value = n;
      fetchProductos();
    };

    const cambiarPorPagina = () => {
      pagina.value = 1;
      fetchProductos();
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar medidas
    |--------------------------------------------------------------------------
    */
    const fetchMedidas = async () => {
      try {
        const response = await api.get(
          '/getMedida'
        );

        Medidas.value = Array.isArray(
          response.data
        )
          ? response.data
          : response.data?.data || [];
      } catch (e) {
        error.value = 'Error al cargar medidas.';
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar categorías
    |--------------------------------------------------------------------------
    */
    const fetchCategorias = async () => {
      try {
        const response = await api.get(
          '/getCategoria'
        );

        Categorias.value = Array.isArray(
          response.data
        )
          ? response.data
          : response.data?.data || [];
      } catch (e) {
        error.value = 'Error al cargar categorías.';
       
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar información fiscal
    |--------------------------------------------------------------------------
    */
    const fetchObjetos = async () => {
      try {
        const response = await api.get(
          '/getexento'
        );

        Objetos.value =
          response.data?.objeto_impuesto || [];

        Impuestos.value =
          response.data?.impuestos || [];

        TiposFactor.value =
          response.data?.tipo_factor || [];
      } catch (e) {
        error.value = 'Error al cargar información.';
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Cargar configuraciones de impuestos
    |--------------------------------------------------------------------------
    */
    const cargarImpuestoConfiguraciones =
      async () => {
        try {
          const response = await api.get(
            '/impuesto-configuraciones'
          );

          ImpuestoConfiguraciones.value =
            extraerConfiguraciones(
              response.data
            );
        } catch (e) {
          ImpuestoConfiguraciones.value = [];

        

          error.value =
            'No fue posible cargar las configuraciones de impuesto.';
        }
      };

    /*
    |--------------------------------------------------------------------------
    | Cargar catálogos
    |--------------------------------------------------------------------------
    */
    const cargarCatalogos = async () => {
      await Promise.all([
        Medidas.value.length === 0
          ? fetchMedidas()
          : Promise.resolve(),

        Categorias.value.length === 0
          ? fetchCategorias()
          : Promise.resolve(),

        Objetos.value.length === 0 ||
        Impuestos.value.length === 0 ||
        TiposFactor.value.length === 0
          ? fetchObjetos()
          : Promise.resolve(),

        ImpuestoConfiguraciones.value.length === 0
          ? cargarImpuestoConfiguraciones()
          : Promise.resolve()
      ]);
    };

    /*
    |--------------------------------------------------------------------------
    | Limpiar formulario
    |--------------------------------------------------------------------------
    */
    const limpiarFormulario = () => {
      ProductoCodigo.value = '';
      ProductoNombre.value = '';
      ProductoPrecioUnitario.value = 0;
      ProductoPrecioCompra.value = 0;
      ProductoGanancia.value = 0;
      ProductoStock.value = 0;
      ProductoActivo.value = true;
      ProductoCategoria.value = '';
      ProductoMedida.value = '';
      ProductoClavePS.value = '';
      ProductoClaveUnidad.value = '';
      ProductoObjetoId.value = '';
      ProductoImpuestoId.value = '';
      ProductoTipoFactorId.value = '';
      ProductoImpuestoConfiguracionId.value = '';
      editingId.value = null;
    };

    /*
    |--------------------------------------------------------------------------
    | Abrir modal de nuevo producto
    |--------------------------------------------------------------------------
    */
    const openAddModal = async () => {
      if (!permisos.value?.crear) {
        error.value =
          'No tienes permiso para crear productos.';

        return;
      }

      try {
        error.value = null;

        await cargarCatalogos();

        limpiarFormulario();

        modalTitle.value = 'Nuevo Producto';
        modalVisible.value = true;
      } catch (e) {
       

        error.value =
          'No fue posible cargar los catálogos del producto.';
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Abrir modal de edición
    |--------------------------------------------------------------------------
    */
    const openEditModal = async (producto) => {
      if (!permisos.value?.actualizar) {
        error.value =
          'No tienes permiso para editar productos.';

        return;
      }

      try {
        error.value = null;

        const [response] = await Promise.all([
          api.get(
            `/productos/${producto.id}`
          ),

          cargarCatalogos()
        ]);

        const p =
          response.data?.data ||
          response.data;

        ProductoCodigo.value =
          p.codigo_producto || '';

        ProductoNombre.value =
          p.nombre || '';

        ProductoPrecioUnitario.value =
          Number(p.precio_unitario || 0);

        ProductoPrecioCompra.value =
          Number(p.precio_compra || 0);

        ProductoGanancia.value =
          Number(p.ganancia || 0);

        ProductoStock.value =
          Number(p.stock || 0);

        ProductoActivo.value =
          Boolean(p.activo);

        ProductoCategoria.value =
          p.id_categoria ?? '';

        ProductoMedida.value =
          p.id_medida ?? '';

        ProductoClavePS.value =
          p.clave_ps || '';

        ProductoClaveUnidad.value =
          p.clave_unidad || '';

        ProductoObjetoId.value =
          p.objeto_id ??
          p.objeto_impuesto_id ??
          '';

        ProductoImpuestoId.value =
          p.impuesto_id ?? '';

        ProductoTipoFactorId.value =
          p.tipofac_id ??
          p.tipo_factor_id ??
          '';

        ProductoImpuestoConfiguracionId.value =
          p.impuesto_configuracion_id ??
          p.configuracion_impuesto_id ??
          p.impuesto_configuracion?.id ??
          '';

        editingId.value = p.id;

        modalTitle.value =
          'Editar Producto';

        modalVisible.value = true;
      } catch (e) {
        

        error.value =
          'No fue posible cargar la información del producto.';
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Guardar o actualizar producto
    |--------------------------------------------------------------------------
    */
    const saveProducto = async () => {
      if (
        editingId.value &&
        !permisos.value?.actualizar
      ) {
        error.value =
          'No tienes permiso para actualizar productos.';

        return;
      }

      if (
        !editingId.value &&
        !permisos.value?.crear
      ) {
        error.value =
          'No tienes permiso para crear productos.';

        return;
      }

      guardando.value = true;
      error.value = null;

      try {
        const payload = {
          codigo_producto:
            ProductoCodigo.value,

          nombre:
            ProductoNombre.value,

          precio_unitario:
            ProductoPrecioUnitario.value,

          precio_compra:
            ProductoPrecioCompra.value,

          ganancia:
            ProductoGanancia.value,

          stock:
            ProductoStock.value,

          activo:
            ProductoActivo.value,

          id_categoria:
            ProductoCategoria.value === ''
              ? null
              : ProductoCategoria.value,

          id_medida:
            ProductoMedida.value === ''
              ? null
              : ProductoMedida.value,

          clave_unidad:
            ProductoClaveUnidad.value,

          clave_ps:
            ProductoClavePS.value,

          objeto_id:
            ProductoObjetoId.value === ''
              ? null
              : ProductoObjetoId.value,

          impuesto_id:
            ProductoImpuestoId.value === ''
              ? null
              : ProductoImpuestoId.value,

          tipofac_id:
            ProductoTipoFactorId.value === ''
              ? null
              : ProductoTipoFactorId.value,

          impuesto_configuracion_id:
            ProductoImpuestoConfiguracionId.value === ''
              ? null
              : ProductoImpuestoConfiguracionId.value
        };

        if (editingId.value) {
          await api.put(
            `/productos/${editingId.value}`,
            payload
          );
        } else {
          await api.post(
            '/productos',
            payload
          );
        }

        modalVisible.value = false;

        await fetchProductos();
      } catch (e) {
        error.value =
          'Error al guardar producto: ' +
          (
            e.response?.data?.message ||
            e.message
          );

      } finally {
        guardando.value = false;
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Solicitar eliminación
    |--------------------------------------------------------------------------
    */
    const deleteProducto = (id) => {
      if (!permisos.value?.eliminar) {
        error.value =
          'No tienes permiso para eliminar productos.';

        return;
      }

      deletingId.value = id;

      confirmMessage.value =
        '¿Seguro que deseas eliminar este producto?';

      confirmModalVisible.value = true;
    };

    /*
    |--------------------------------------------------------------------------
    | Confirmar eliminación
    |--------------------------------------------------------------------------
    */
    const onConfirmDelete = async () => {
      if (!permisos.value?.eliminar) {
        error.value =
          'No tienes permiso para eliminar productos.';

        confirmModalVisible.value = false;
        return;
      }

      if (!deletingId.value) {
        return;
      }

      guardando.value = true;
      error.value = null;

      try {
        await api.delete(
          `/productos/${deletingId.value}`
        );

        await fetchProductos();
      } catch (e) {
        error.value =
          'Error al eliminar producto: ' +
          (
            e.response?.data?.message ||
            e.message
          );

       
      } finally {
        guardando.value = false;
        deletingId.value = null;
        confirmModalVisible.value = false;
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Inicialización
    |--------------------------------------------------------------------------
    */
    onMounted(async () => {
      await fetchPermisos();

      if (permisos.value?.listar) {
        await fetchProductos();
      }
    });

    /*
    |--------------------------------------------------------------------------
    | Salir del módulo: no dejar datos en memoria
    |--------------------------------------------------------------------------
    | Se descarta el listado y los catálogos, y se invalida cualquier petición
    | en vuelo para que su respuesta no reviva el estado ya desmontado.
    */
    onBeforeUnmount(() => {
      peticionActual++;

      Productos.value = [];
      Medidas.value = [];
      Categorias.value = [];
      Objetos.value = [];
      Impuestos.value = [];
      TiposFactor.value = [];
      ImpuestoConfiguraciones.value = [];

      meta.value = {
        total: 0,
        current_page: 1,
        per_page: porPagina.value,
        last_page: 1
      };
    });

    return {
      sidebarOpen,

      permisos,

      Productos,
      Medidas,
      Categorias,
      Objetos,
      Impuestos,
      TiposFactor,
      ImpuestoConfiguraciones,

      loading,
      error,

      modalVisible,
      modalTitle,
      editingId,

      ProductoCodigo,
      ProductoNombre,
      ProductoPrecioUnitario,
      ProductoPrecioCompra,
      ProductoGanancia,
      ProductoStock,
      ProductoActivo,
      ProductoCategoria,
      ProductoMedida,
      ProductoClavePS,
      ProductoClaveUnidad,
      ProductoObjetoId,
      ProductoImpuestoId,
      ProductoTipoFactorId,
      ProductoImpuestoConfiguracionId,

      confirmModalVisible,
      confirmMessage,

      filtroCodigo,
      filtroNombre,
      filtroDesde,
      filtroHasta,
      porPagina,
      meta,
      guardando,
      hayFiltros,
      totalColumnas,

      numeroFila,
      fechaAlta,
      aplicarFiltros,
      limpiarFiltros,
      irPagina,
      cambiarPorPagina,

      obtenerNombreConfiguracion,
      openAddModal,
      openEditModal,
      saveProducto,
      deleteProducto,
      onConfirmDelete
    };
  }
};
</script>


<style scoped>
.main-content {
  flex: 1;
  margin-top: 60px;
  transition: margin-left 0.3s ease;
}

@media (min-width: 992px) {
  .main-content {
    margin-left: 250px;
  }
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1019;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

main {
  min-height: calc(100vh - 60px);
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.productos-filtros .form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
}

.productos-paginacion {
  border-top: 1px solid #eef0f3;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
